import React, { useCallback } from 'react';
import config from '@config/index';
import { useVendor } from '@context/VendorContext';
import { getPreviewUrl } from '@features/goodfynd-sites/utils/goodfynd-sites-util';
import { useGetSiteProfile } from '@features/settings/organization-site/hooks/useGetSiteProfile';
import { usePublishSiteProfile } from '@features/settings/organization-site/hooks/usePublishSiteProfile';
import { useUnpublishSiteProfile } from '@features/settings/organization-site/hooks/useUnpublishSiteProfile';
import { FlexView, Loading, Text } from '@goodfynd/ui';
import { Button } from '@goodfynd/ui';
import { Link } from '@goodfynd/ui';
import { useOrganizationHasFeatureAccess } from '@hooks/useFeatureAccess';
import { getDashboardRoutePath } from '@utils/routes-util';

import routes from '../../../config/routes';
import { OverviewCard } from '../overview-card';

export default function GoodfyndSitesOverview() {
  const { vendor } = useVendor();

  const hasOrganizationSiteAccess = useOrganizationHasFeatureAccess(
    config.features.organizationSite
  );
  const hasOrganizationSiteOrderingAccess = useOrganizationHasFeatureAccess(
    config.features.organizationSiteOrdering
  );
  const hasAccess =
    hasOrganizationSiteAccess || hasOrganizationSiteOrderingAccess;

  const { isLoading, siteProfile, data } = useGetSiteProfile(
    vendor.organizationId,
    'preview',
    { enabled: hasAccess }
  );

  const publishedProfile = data?.published;
  const isPublished = !!publishedProfile;

  const { isPublishingSiteProfile, publishSiteProfile } = usePublishSiteProfile(
    vendor.organizationId
  );

  const { isUnpublishingSiteProfile, unpublishSiteProfile } =
    useUnpublishSiteProfile(vendor.organizationId);

  const handlePreview = useCallback(() => {
    if (siteProfile?.websiteUrl) {
      const previewUrl = getPreviewUrl(new URL(siteProfile.websiteUrl).host);
      window.open(previewUrl, '_blank');
    }
  }, [siteProfile?.websiteUrl]);

  const handleViewLive = useCallback(() => {
    if (publishedProfile?.websiteUrl) {
      window.open(publishedProfile.websiteUrl, '_blank');
    }
  }, [publishedProfile?.websiteUrl]);

  if (!hasAccess) {
    return null;
  }

  return (
    <OverviewCard
      id="overview-goodfynd-sites"
      showDateFilter={false}
      title="Your Website"
      titleRight={
        <Link
          href={getDashboardRoutePath(
            routes.dashboard.settings.website,
            vendor.nameId
          )}
        >
          <Text as="subhead3" inherit>
            Manage
          </Text>
        </Link>
      }
    >
      {isLoading ? (
        <FlexView align="center" height="100%" justify="center">
          <Loading type="circle" />
        </FlexView>
      ) : (
        <FlexView direction="column" gap={16} height="100%" justify="center">
          <FlexView direction="column" gap={2}>
            <Text as="body2">Status:</Text>
            <Text
              as="subhead2"
              color={isPublished ? 'TextIconFavorable' : 'TextIconWarning'}
            >
              {isPublished ? 'Published' : 'Unpublished'}
            </Text>
          </FlexView>

          {siteProfile?.websiteUrl && (
            <FlexView direction="column" gap={2} width="100%">
              <Text as="body2">URL:</Text>
              <Text
                as="body2"
                href={new URL(siteProfile.websiteUrl).host}
                title={siteProfile.websiteUrl}
                truncate={1}
              >
                <Text tag="span">{siteProfile.websiteUrl}</Text>
              </Text>
            </FlexView>
          )}

          <FlexView direction="column" gap={8} style={{ marginTop: 'auto' }}>
            {isPublished ? (
              <FlexView gap={8}>
                <Button
                  onClick={handleViewLive}
                  size="small"
                  style={{ flex: 1 }}
                >
                  View Live
                </Button>

                <Button
                  onClick={handlePreview}
                  rank="secondary"
                  size="small"
                  style={{ flex: 1 }}
                >
                  Preview
                </Button>

                <Button
                  busy={isUnpublishingSiteProfile}
                  color="neutral"
                  onClick={unpublishSiteProfile}
                  size="small"
                  style={{ flex: 1 }}
                >
                  Unpublish
                </Button>
              </FlexView>
            ) : (
              <FlexView gap={8}>
                <Button
                  onClick={handlePreview}
                  rank="secondary"
                  size="small"
                  style={{ flex: 1 }}
                >
                  Preview
                </Button>

                <Button
                  busy={isPublishingSiteProfile}
                  onClick={publishSiteProfile}
                  rank="primary"
                  size="small"
                  style={{ flex: 1 }}
                >
                  Publish
                </Button>
              </FlexView>
            )}
          </FlexView>
        </FlexView>
      )}
    </OverviewCard>
  );
}
